import { useCallback, useEffect, useRef, useState } from "react";
import { getAccountDetails } from "@api-routes/bff";
import { useFetch } from "@common/hook/useFetch";
import { useGetAppointmentList, useGetRecommendationBanner } from "@modules/eirCustomersFacade/swr";
import { useGetNotificationCount } from "@modules/notificationService/swr";
import { useGetServiceWithESimToActivateList } from "@features/ManageSim/hook/useGetServiceWithESimToActivateList";
import { useDeviceType } from "@hook/useDeviceType";
import { EStatus, IAccountDetailsPage } from "@pages/AccountDetails/interface/IAccountDetailsPage";

export interface IAlertCount {
    count: number;
}

export const useHomePage = () => {
    const [count, setCount] = useState(0);
    const [recommendationBanner, setRecommendationBanner] = useState<string>();
    const {
        data: recommendationBannerData,
        isLoading: isRecommendationBannerLoading,
        mutate: mutateRecommendationBanner,
    } = useGetRecommendationBanner();
    const { serviceWithESimToActivateList, isLoading: isESimLoading } = useGetServiceWithESimToActivateList();
    const { data: appointmentList = [], isLoading: isAppointmentLoading, mutate } = useGetAppointmentList();
    const { data: alert } = useGetNotificationCount();
    const [, { get: getAccountDetailsPage }] = useFetch();
    const [showBroadbandTechSupport, setShowBroadbandTechSupport] = useState(false);
    const homeRef = useRef<HTMLDivElement>(null);
    const homeTop = homeRef.current?.getBoundingClientRect().top;
    const [elementRef, setElementRef] = useState<HTMLDivElement | null>(null);
    const { isMobile } = useDeviceType();
    const isLoading = isESimLoading || isRecommendationBannerLoading || isAppointmentLoading;
    const [backgroundHeight, setBackgroundHeight] = useState(0);

    const updateBackgroundHeightFromElement = (element: HTMLDivElement | null) => {
        if (homeTop && element) {
            setBackgroundHeight(element.getBoundingClientRect().top - homeTop + (isMobile ? 20 : 40));
            setElementRef(element);
        }
    };

    const updateBackgroundHeight = () => {
        updateBackgroundHeightFromElement(elementRef);
    };

    useEffect(() => {
        setCount(alert?.count ?? 0);
        if (recommendationBannerData && typeof recommendationBannerData === "string") {
            setRecommendationBanner(recommendationBannerData);
        } else {
            setRecommendationBanner(undefined);
        }
    }, [alert?.count, recommendationBannerData]);

    const fetchBroadbandTechSupportVisibility = useCallback(() => {
        getAccountDetailsPage<IAccountDetailsPage>(getAccountDetails()).then((details) => {
            setShowBroadbandTechSupport(details?.broadbandSubscription?.status === EStatus.ACTIVE);
        });
    }, [getAccountDetailsPage]);

    useEffect(() => {
        fetchBroadbandTechSupportVisibility();
    }, [fetchBroadbandTechSupportVisibility]);

    return {
        appointmentList,
        backgroundHeight,
        count,
        homeRef,
        isLoading,
        mutate,
        recommendationBanner,
        mutateRecommendationBanner,
        serviceWithESimToActivateList,
        showBroadbandTechSupport,
        updateBackgroundHeight,
        updateBackgroundHeightFromElement,
    };
};
